import React, { Suspense } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { PlusCircle, Trash2 } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';

const durationOptions = [
  15, 30, 45, 60, 90, 120
] as const;

const meetingMethodSchema = z.object({
  type: z.enum(['in_person', 'video', 'phone']),
  details: z.string().optional(),
});

const customFieldSchema = z.object({
  label: z.string().min(1, 'Label is required'),
  type: z.enum(['text', 'select', 'checkbox']),
  required: z.boolean(),
  options: z.array(z.string()).optional(),
});

const serviceSchema = z.object({
  name: z.string().min(2, 'Service name must be at least 2 characters'),
  duration: z.number().min(15, 'Duration must be at least 15 minutes'),
  price: z.number().min(0, 'Price cannot be negative'),
  description: z.string().min(10, 'Description must be at least 10 characters'),
  meetingMethods: z.array(meetingMethodSchema).min(1, 'At least one meeting method is required'),
  customFields: z.array(customFieldSchema).optional(),
  isActive: z.boolean(),
});

type ServiceFormValues = z.infer<typeof serviceSchema>;

const ServiceEditorContent: React.FC<{ serviceId?: string }> = ({ serviceId }) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: service, isLoading: isLoadingService } = useQuery({
    queryKey: ['service', serviceId],
    queryFn: async () => {
      if (!serviceId) return null;
      
      // Try to get data from cache first
      const cachedData = queryClient.getQueryData(['services', user?.id]) as Service[];
      const cachedService = cachedData?.find(s => s.id === serviceId);
      if (cachedService) {
        return cachedService;
      }

      const { data, error } = await supabase
        .from('services')
        .select('*')
        .eq('id', serviceId)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!serviceId && !!user?.id,
    staleTime: 1000 * 60 * 5,
    cacheTime: 1000 * 60 * 30,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  const form = useForm<ServiceFormValues>({
    resolver: zodResolver(serviceSchema),
    defaultValues: {
      name: '',
      duration: 30,
      price: 0,
      description: '',
      meetingMethods: [{ type: 'video' }],
      customFields: [],
      isActive: true,
    },
  });

  React.useEffect(() => {
    if (service) {
      form.reset({
        name: service.name,
        duration: service.duration,
        price: service.price,
        description: service.description,
        meetingMethods: service.meeting_methods,
        customFields: service.custom_fields,
        isActive: service.is_active,
      });
    }
  }, [service, form]);

  const updateService = useMutation({
    mutationFn: async (values: ServiceFormValues) => {
      if (!user?.id) throw new Error('User not authenticated');
      
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      if (!uuidRegex.test(user.id)) {
        throw new Error('Invalid user ID format');
      }

      console.log('Form values:', values);
      console.log('User ID:', user.id);

      const serviceData = {
        user_id: user.id,
        name: values.name,
        duration: values.duration,
        price: values.price,
        description: values.description,
        meeting_methods: values.meetingMethods,
        custom_fields: values.customFields,
        is_active: values.isActive,
        updated_at: new Date().toISOString(),
      };

      console.log('Service data:', serviceData);

      let query = supabase.from('services');
      
      try {
        if (serviceId) {
          console.log('Updating existing service:', serviceId);
          const { data, error } = await query
            .update(serviceData)
            .eq('id', serviceId)
            .select()
            .single();
          
          if (error) {
            console.error('Update error:', error);
            throw error;
          }
          console.log('Update response:', data);
          return data;
        } else {
          console.log('Creating new service');
          const { data, error } = await query
            .insert(serviceData)
            .select()
            .single();
          
          if (error) {
            console.error('Insert error:', error);
            throw error;
          }
          console.log('Insert response:', data);
          return data;
        }
      } catch (error) {
        console.error('Service operation failed:', error);
        throw error;
      }
    },
    onSuccess: (data) => {
      console.log('Service saved successfully:', data);
      queryClient.invalidateQueries({ queryKey: ['services'] });
      if (serviceId) {
        queryClient.invalidateQueries({ queryKey: ['service', serviceId] });
      }
      toast.success(serviceId ? 'Service updated successfully' : 'Service created successfully');
    },
    onError: (error: any) => {
      console.error('Service update error:', {
        error,
        message: error?.message,
        details: error?.details,
        hint: error?.hint,
        code: error?.code
      });

      let errorMessage = 'Failed to save service. ';
      if (error?.message === 'Invalid user ID format') {
        errorMessage = 'Authentication error. Please try logging out and back in.';
      } else if (error?.code === '22P02') {
        errorMessage += 'Invalid data format. ';
      }
      if (error?.message && error?.message !== 'Invalid user ID format') {
        errorMessage += error.message;
      }
      if (error?.details) {
        errorMessage += ` (${error.details})`;
      }

      toast.error(errorMessage);
    },
  });

  const onSubmit = (values: ServiceFormValues) => {
    updateService.mutate(values);
  };

  const LoadingSkeleton = () => (
    <div className="space-y-6">
      <div className="space-y-2">
        <Skeleton className="h-4 w-[100px]" />
        <Skeleton className="h-10 w-full" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Skeleton className="h-4 w-[80px]" />
          <Skeleton className="h-10 w-full" />
        </div>
        <div className="space-y-2">
          <Skeleton className="h-4 w-[60px]" />
          <Skeleton className="h-10 w-full" />
        </div>
      </div>
      <div className="space-y-2">
        <Skeleton className="h-4 w-[120px]" />
        <Skeleton className="h-32 w-full" />
      </div>
      <Skeleton className="h-10 w-[120px]" />
    </div>
  );

  if (isLoadingService) {
    return <LoadingSkeleton />;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{serviceId ? 'Edit Service' : 'Create New Service'}</CardTitle>
        <CardDescription>
          Configure your service details and booking requirements
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Service Name</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g., Consultation Call" {...field} />
                  </FormControl>
                  <FormDescription>
                    A clear name that describes your service
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormField
                control={form.control}
                name="duration"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Duration (minutes)</FormLabel>
                    <Select
                      onValueChange={(value) => field.onChange(parseInt(value))}
                      defaultValue={field.value.toString()}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select duration" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {durationOptions.map((duration) => (
                          <SelectItem key={duration} value={duration.toString()}>
                            {duration} minutes
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="price"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Price</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="0.00"
                        {...field}
                        onChange={(e) => field.onChange(parseFloat(e.target.value))}
                      />
                    </FormControl>
                    <FormDescription>
                      Set to 0 for free services
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Describe what this service includes..."
                      className="min-h-[100px]"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    Help your clients understand what to expect
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="isActive"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Active Service</FormLabel>
                    <FormDescription>
                      Clients can book this service when active
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <Button
              type="submit"
              className="w-full md:w-auto"
              disabled={updateService.isPending}
            >
              {updateService.isPending ? 'Saving...' : 'Save Service'}
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};

export const ServiceEditor: React.FC<{ serviceId?: string }> = (props) => {
  return (
    <Suspense fallback={<LoadingSkeleton />}>
      <ServiceEditorContent {...props} />
    </Suspense>
  );
}; 